import { classifyIp, type IpKind } from "./ipClassification";

export type IpFlowRecord = {
  sourceIps?: unknown;
  destinationIps?: unknown;
  createdDateTime?: unknown;
};

export type IpFlowLink = {
  source: string;
  destination: string;
  count: number;
  sourceKind: IpKind;
  destinationKind: IpKind;
};

export type IpFlowCenter = {
  timeZone: string;
  latitude: number | null;
  longitude: number | null;
  label: string;
};

export type IpFlowNode = {
  ip: string;
  kind: IpKind;
  latitude?: number | null;
  longitude?: number | null;
  city?: string;
  countryCode?: string;
  asSource: number;
  asDestination: number;
  firstSeen: number;
};

export type IpFlowEvent = {
  at: number;
  source: string;
  destination: string;
  severity?: string;
  sourceKind: IpKind;
  destinationKind: IpKind;
};

export type IpFlowStats = {
  recordCount: number;
  eventCount: number;
  internalCount: number;
  externalCount: number;
};

export type IpFlowMatrix = {
  window?: string;
  generatedAt?: string;
  center?: IpFlowCenter | null;
  nodes: IpFlowNode[];
  links: IpFlowLink[];
  events: IpFlowEvent[];
  recordCount: number;
  pairCount: number;
  stats?: IpFlowStats;
};

export const EMPTY_IP_FLOW_MATRIX: IpFlowMatrix = {
  window: "24h",
  generatedAt: "",
  center: null,
  nodes: [],
  links: [],
  events: [],
  recordCount: 0,
  pairCount: 0,
  stats: {
    recordCount: 0,
    eventCount: 0,
    internalCount: 0,
    externalCount: 0,
  },
};

export const MAX_PLAYBACK_EVENTS = 48;

function extractIps(value: unknown): string[] {
  const raw = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? [value]
      : [];
  const seen = new Set<string>();
  const ips: string[] = [];

  for (const item of raw) {
    if (typeof item !== "string") continue;
    const ip = item.trim();
    if (!ip || seen.has(ip)) continue;
    seen.add(ip);
    ips.push(ip);
  }

  return ips;
}

export function parseFlowTimestamp(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value < 1_000_000_000_000 ? value * 1000 : value;
  }
  if (typeof value === "string" && value.trim()) {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) {
      return numeric < 1_000_000_000_000 ? numeric * 1000 : numeric;
    }
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return fallback;
}

export function buildIpFlowMatrix(
  records: IpFlowRecord[],
  maxEvents = MAX_PLAYBACK_EVENTS,
): IpFlowMatrix {
  const events: IpFlowEvent[] = [];
  const asSource = new Map<string, number>();
  const asDestination = new Map<string, number>();
  const firstSeen = new Map<string, number>();
  const pairCounts = new Map<
    string,
    { source: string; destination: string; count: number }
  >();
  let pairCount = 0;

  records.forEach((record, index) => {
    const sources = extractIps(record.sourceIps);
    const destinations = extractIps(record.destinationIps);
    if (sources.length === 0 || destinations.length === 0) return;
    const at = parseFlowTimestamp(record.createdDateTime, index);

    for (const source of sources) {
      asSource.set(source, (asSource.get(source) ?? 0) + destinations.length);
      firstSeen.set(source, Math.min(firstSeen.get(source) ?? at, at));
      for (const destination of destinations) {
        asDestination.set(
          destination,
          (asDestination.get(destination) ?? 0) + 1,
        );
        firstSeen.set(
          destination,
          Math.min(firstSeen.get(destination) ?? at, at),
        );
        const key = `${source}\u0000${destination}`;
        const existing = pairCounts.get(key);
        if (existing) {
          existing.count += 1;
        } else {
          pairCounts.set(key, { source, destination, count: 1 });
        }
        pairCount += 1;
        events.push({
          at,
          source,
          destination,
          sourceKind: classifyIp(source),
          destinationKind: classifyIp(destination),
        });
      }
    }
  });

  events.sort(
    (left, right) =>
      left.at - right.at ||
      left.source.localeCompare(right.source) ||
      left.destination.localeCompare(right.destination),
  );

  const playback =
    events.length > maxEvents ? events.slice(events.length - maxEvents) : events;
  const usedIps = new Set<string>();
  for (const event of playback) {
    usedIps.add(event.source);
    usedIps.add(event.destination);
  }

  const nodes = [...usedIps]
    .sort(
      (left, right) =>
        (firstSeen.get(left) ?? 0) - (firstSeen.get(right) ?? 0) ||
        left.localeCompare(right),
    )
    .map((ip) => ({
      ip,
      kind: classifyIp(ip),
      asSource: asSource.get(ip) ?? 0,
      asDestination: asDestination.get(ip) ?? 0,
      firstSeen: firstSeen.get(ip) ?? 0,
    }));

  const keep = new Set(nodes.map((node) => node.ip));
  const links = [...pairCounts.values()]
    .filter((link) => keep.has(link.source) && keep.has(link.destination))
    .sort((left, right) => right.count - left.count)
    .map((link) => ({
      source: link.source,
      destination: link.destination,
      count: link.count,
      sourceKind: classifyIp(link.source),
      destinationKind: classifyIp(link.destination),
    }));

  return {
    nodes,
    links,
    events: playback,
    recordCount: records.length,
    pairCount,
  };
}
